/*
 * @providesModule CountingLabel
 *
 */

import Label from 'Label';
import SKZConstants from 'SKZConstants';
import GradientLabel from 'GradientLabel';

import PropTypes from 'prop-types';
import AsyncStorage from '@react-native-async-storage/async-storage';

import React, {Component} from 'react';

import {Platform, StyleSheet} from 'react-native';

import sprintfJS from 'sprintf-js';
const sprintf = sprintfJS.sprintf;
const ONE_SECOND = SKZConstants.MS_PER_SEC;
const LOWER_BOUND = 15;

export default class CountingLabel extends Component {
  static propTypes = {
    identifier: PropTypes.string.isRequired,
    value: PropTypes.number,
    formatter: PropTypes.func,
    style: PropTypes.any,
    prefix: PropTypes.string,
    postfix: PropTypes.string,
    duration: PropTypes.number,
    precision: PropTypes.number,
    countDownEnabled: PropTypes.bool,
    countUpEnabled: PropTypes.bool,
    animationCurve: PropTypes.oneOf(['linear', 'ease-in', 'ease-out', 'ease-in-ease-out']),
    forceAnimation: PropTypes.bool,
    isLabelFormatted: PropTypes.bool,
  };

  static defaultProps = {
    prefix: '',
    postfix: '',
    formatter: null,
    duration: 2000,
    precision: 0,
    animationCurve: 'ease-in',
    countDownEnabled: false,
    countUpEnabled: true,
    forceAnimation: false,
    isLabelFormatted: false,
  };

  constructor(props: Object) {
    super(props);
    this.componentIsUnmounted = true;
    this.state = {
      currentValue: null,
    };
    this._setUpAnimationValues();
  }

  componentDidMount() {
    this.componentIsUnmounted = false;
    this.startLabelTimer();
  }

  componentWillUnmount() {
    this.componentIsUnmounted = true;
    this._storeTargetValue();
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  componentDidUpdate(prevProps: unknown) {
    // We should only trigger this on a prop update not on a state update
    // Since startLabelTimer actually updates state so it endlessly loops on itself
    // Causing the weird stutter that we previously saw with the lifecycle methods change
    if (prevProps.value != this.props.value) {
      setTimeout(() => {
        clearInterval(this.timer);
        this.timer = null;
        this.startLabelTimer();
      }, ONE_SECOND);
    }
    this._storeTargetValue();
  }

  shouldComponentUpdate(nextProps: unknown, nextState: unknown): boolean {
    if (nextProps.value != this.props.value) {
      return true;
    }

    if (this.state.currentValue !== null) {
      let newNumber = '0';
      let oldNumber = '0';
      if (this.props.formatter) {
        newNumber = this.props.formatter(this.state.currentValue);
        oldNumber = this.props.formatter(nextState.currentValue);
      } else {
        const formatString = sprintf('%%0.0%df', this.props.precision);
        newNumber = sprintf(formatString, this.state.currentValue);
        oldNumber = sprintf(formatString, nextState.currentValue);
      }

      if (newNumber == oldNumber) {
        return false;
      }
    }

    return true;
  }

  async _setUpAnimationValues() {
    let startingValue = 0;
    const kCountingLabelKey = 'CountingLabel_' + this.props.identifier;
    const targetValue = this.props.value;

    if (!this.props.forceAnimation) {
      try {
        startingValue = JSON.parse(await AsyncStorage.getItem(kCountingLabelKey));
      } catch (e) {
        console.debug('CountingLabel failed to get key: ' + kCountingLabelKey + ' error: ' + e);
        startingValue = 0;
      }
    }

    if (this.props.countUpEnabled && targetValue - startingValue <= LOWER_BOUND) {
      this.duration = Math.floor(this.props.duration * 0.65);
      this.animationCurve = 'linear';
    } else {
      this.duration = this.props.duration;
      this.animationCurve = this.props.animationCurve;
    }
  }

  _storeTargetValue() {
    if (!this.props.forceAnimation) {
      try {
        if (this.state.currentValue) {
          const parsedValue = JSON.stringify(this.state.currentValue);
          if (parsedValue) {
            const kCountingLabelKey = 'CountingLabel_' + this.props.identifier;
            AsyncStorage.setItem(kCountingLabelKey, parsedValue);
          }
        }
      } catch (e) {
        console.debug('Failed storing target value');
      }
    }
  }

  // BUG: Some balance bar items skip to the full value, have not been able to discern
  // What triggers it- I've seen an account with only the Z balance animate and I've seen
  // an account with only Ticketz/Cash update
  // TODO: https://skillzinc.atlassian.net/browse/PS-51461
  async startLabelTimer() {
    if (this.timer || this.componentIsUnmounted) {
      return;
    }

    let startingValue = 0;
    const kCountingLabelKey = 'CountingLabel_' + this.props.identifier;
    const targetValue = this.props.value;

    if (!this.props.forceAnimation) {
      try {
        startingValue = JSON.parse(await AsyncStorage.getItem(kCountingLabelKey));
      } catch (e) {
        startingValue = 0;
      }
    }

    if (this.timer || this.componentIsUnmounted) {
      return;
    }

    if (!this.props.countDownEnabled && targetValue <= startingValue) {
      if (targetValue != this.state.currentValue) {
        this.setState({currentValue: targetValue});
      }
      return;
    }
    if (!this.props.countUpEnabled && targetValue >= startingValue) {
      if (targetValue != this.state.currentValue) {
        this.setState({currentValue: targetValue});
      }
      return;
    }
    const startTime = new Date().getTime();
    const delta = targetValue - startingValue;

    const FIFTY_MILLISECONDS = 50;

    this.timer = setInterval(() => {
      if (this.componentIsUnmounted) {
        clearInterval(this.timer);
        this.timer = null;
        return;
      }
      const elapsedTime = new Date().getTime() - startTime;

      if (elapsedTime >= this.duration) {
        clearInterval(this.timer);
        this.timer = null;
      }

      const percentage = Math.min(elapsedTime / this.duration, 1.0);
      let updatedValue = this.state.currentValue;
      switch (this.animationCurve) {
        case 'linear':
          updatedValue = percentage * delta + startingValue;
          break;

        default:
        case 'ease-in':
          updatedValue = percentage * percentage * percentage * delta + startingValue;
          break;

        case 'ease-out':
          updatedValue =
            (1 - (1 - percentage) * (1 - percentage) * (1 - percentage)) * delta + startingValue;
          break;

        case 'ease-in-ease-out':
          {
            let t = percentage * 2;
            if (t < 1) {
              updatedValue = (delta / 2) * t * t * t + startingValue;
            } else {
              t -= 2;
              updatedValue = (delta / 2) * (t * t * t + 2) + startingValue;
            }
          }
          break;
      }
      this.setState({currentValue: updatedValue});
    }, FIFTY_MILLISECONDS);
  }

  render(): React.ReactNode {
    let formattedNumber = '';
    if (this.state.currentValue !== null) {
      if (this.props.formatter) {
        formattedNumber = this.props.formatter(this.state.currentValue);
      } else {
        const formatString = sprintf('%%0.0%df', this.props.precision);
        formattedNumber = sprintf(formatString, this.state.currentValue);
      }
    }
    const propStyles = StyleSheet.flatten(this.props.style);
    const shouldUseGradient = Platform.OS == 'ios' && propStyles.gradientColors;
    const style = {
      ...propStyles,
      textAlign: propStyles.textAlign || 'center',
      fontFamily: propStyles.fontFamily,
      fontSize: propStyles.fontSize,
      color: shouldUseGradient ? propStyles.gradientColors[0] : propStyles.color,
    };
    const TextContainer = shouldUseGradient ? GradientLabel : Label;

    if (this.props.isLabelFormatted) {
      if (this.timer == null || Platform.OS == 'ios') {
        return (
          <TextContainer style={propStyles} numberOfLines={1}>
            {Number(formattedNumber).toLocaleString()}
          </TextContainer>
        );
      }
      // Use key while animating so that it creates new Label each time - forces resizing code to re run and be accurate in case of major length change
      return (
        <Label style={style} adjustsFontSizeToFit={true} numberOfLines={1} key={formattedNumber}>
          {Number(formattedNumber).toLocaleString()}
        </Label>
      );
    }

    if (this.timer == null || Platform.OS == 'ios') {
      return (
        <TextContainer style={propStyles} numberOfLines={1}>
          {this.props.prefix + formattedNumber + this.props.postfix}
        </TextContainer>
      );
    }
    // Use key while animating so that it creates new Label each time - forces resizing code to re run and be accurate in case of major length change
    return (
      <Label style={style} adjustsFontSizeToFit={true} numberOfLines={1} key={formattedNumber}>
        {this.props.prefix + formattedNumber + this.props.postfix}
      </Label>
    );
  }
}
